import { DATAFEED_API } from '../../config/index.js'
import type { CandleData } from '../components/GraphCandle'

type RawCandle = [number, number, number, number, number]

const MAX_CACHE_ENTRIES = 500
const FETCH_TIMEOUT_MS = 15_000

const dayCache = new Map<string, CandleData[]>()
const inflightRequests = new Map<string, Promise<CandleData[]>>()

function cacheKey(symbol: string, timeframe: string, date: string): string {
  return `${symbol}/${timeframe}/${date}`
}

function cacheSet(key: string, value: CandleData[]): void {
  if (dayCache.has(key)) dayCache.delete(key)
  dayCache.set(key, value)
  while (dayCache.size > MAX_CACHE_ENTRIES) {
    const oldest = dayCache.keys().next().value
    if (oldest !== undefined) dayCache.delete(oldest)
    else break
  }
}

function cacheGet(key: string): CandleData[] | undefined {
  const value = dayCache.get(key)
  if (value === undefined) return undefined
  dayCache.delete(key)
  dayCache.set(key, value)
  return value
}

function transformRaw(raw: RawCandle[]): CandleData[] {
  return raw.map(([time, open, high, low, close]) => ({ time, open, high, low, close }))
}

function formatDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function getDateList(start: Date, end: Date): string[] {
  const dates: string[] = []
  const cursor = new Date(start)
  cursor.setHours(0, 0, 0, 0)
  const endNorm = new Date(end)
  endNorm.setHours(0, 0, 0, 0)

  while (cursor <= endNorm) {
    dates.push(formatDate(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }
  return dates
}

async function fetchSingleDay(
  symbol: string,
  timeframe: string,
  date: string,
): Promise<CandleData[]> {
  const key = cacheKey(symbol, timeframe, date)

  const cached = cacheGet(key)
  if (cached) return cached

  const inflight = inflightRequests.get(key)
  if (inflight) return inflight

  const promise = (async () => {
    try {
      const url = `${DATAFEED_API}/datafeed/candle/${symbol}/${timeframe}/${date}.json`
      const res = await fetch(url, { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) })
      if (!res.ok) return []
      const raw: RawCandle[] = await res.json()
      const candles = transformRaw(raw)
      cacheSet(key, candles)
      return candles
    } catch {
      return []
    } finally {
      inflightRequests.delete(key)
    }
  })()

  inflightRequests.set(key, promise)
  return promise
}

export async function fetchDays(
  symbol: string,
  timeframe: string,
  dates: string[],
): Promise<CandleData[]> {
  const results = await Promise.all(
    dates.map(date => fetchSingleDay(symbol, timeframe, date)),
  )
  return results.flat().sort((a, b) => a.time - b.time)
}

export async function fetchWindow(
  symbol: string,
  timeframe: string,
  centerDate: Date,
  bufferDays: number,
  rangeStart: Date,
  rangeEnd: Date,
): Promise<CandleData[]> {
  const start = new Date(centerDate)
  start.setDate(start.getDate() - bufferDays)
  if (start < rangeStart) start.setTime(rangeStart.getTime())

  const end = new Date(centerDate)
  end.setDate(end.getDate() + bufferDays)
  if (end > rangeEnd) end.setTime(rangeEnd.getTime())

  const dates = getDateList(start, end)
  return fetchDays(symbol, timeframe, dates)
}

export function clearCache(): void {
  dayCache.clear()
  inflightRequests.clear()
}
